import React, { useState } from 'react';
import {
  FaGlasses, FaEye, FaTools, FaCheck, FaBell, FaMobileAlt,
  FaInfoCircle, FaPaperPlane, FaArrowRight, FaTimes, FaPlus, FaPen,
} from 'react-icons/fa';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import api from '../../../services/api';

const PRODUCT_TYPES = [
  { key: 'glasses', icon: FaGlasses, label: 'Lunettes', desc: 'Monture + verres' },
  { key: 'lenses', icon: FaEye, label: 'Lentilles', desc: 'Commande de lentilles' },
  { key: 'sav', icon: FaTools, label: 'SAV', desc: 'Réparation / ajustement' },
  { key: 'custom', icon: FaPen, label: 'Autre', desc: 'Message personnalisé' },
];

const SMS_MAX_CHARS = 160;

const OrderNotifyPage = () => {
  const { brand, brandName, brandLogo, tenantHeaders } = useProContext();
  const TITLE_MAX_CHARS = 50;
  const [step, setStep] = useState(1);
  const [productType, setProductType] = useState(null);
  const [mutuelle, setMutuelle] = useState(null); // 'yes' | 'no' | null
  const [customTitle, setCustomTitle] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [phones, setPhones] = useState([]); // string[] — envois en masse
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const clientName = '';
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { sent, failed, total }

  const getNotifMessage = () => {
    if (productType === 'custom') {
      return customMessage.trim();
    }
    const hello = clientName ? `Bonjour ${clientName}, ` : 'Bonjour, ';
    if (productType === 'glasses') {
      return `${hello}nous avons le plaisir de vous informer que vos lunettes sont prêtes. Vous pouvez passer les récupérer chez ${brandName} aux horaires d'ouverture.`;
    }
    if (productType === 'lenses') {
      return `${hello}nous avons le plaisir de vous informer que vos lentilles sont prêtes. Vous pouvez venir les récupérer chez ${brandName} à tout moment.`;
    }
    if (productType === 'sav') {
      return `${hello}nous avons le plaisir de vous informer que votre SAV est terminé et que votre produit est prêt. Vous pouvez passer le récupérer chez ${brandName} aux horaires d'ouverture.`;
    }
    return `${hello}nous avons le plaisir de vous informer que votre commande est prête chez ${brandName}.`;
  };

  // Étape "détails" requise pour glasses (mutuelle ?) et custom (message)
  const needsDetailsStep = productType === 'glasses' || productType === 'custom';

  const canGoNext = () => {
    if (step === 1) return !!productType;
    if (step === 2) {
      if (productType === 'glasses') return mutuelle !== null;
      if (productType === 'custom') {
        const titleLen = customTitle.trim().length;
        const msgLen = customMessage.trim().length;
        return titleLen > 0 && titleLen <= TITLE_MAX_CHARS && msgLen > 0 && msgLen <= SMS_MAX_CHARS;
      }
      return true;
    }
    if (step === 3) return phones.length > 0;
    return true;
  };

  const addPhone = (raw) => {
    const cleaned = (raw || '').replace(/\D/g, '').slice(0, 10);
    if (cleaned.length !== 10) {
      setPhoneError('Numéro invalide (10 chiffres requis)');
      return false;
    }
    if (phones.includes(cleaned)) {
      setPhoneError('Numéro déjà ajouté');
      return false;
    }
    setPhones([...phones, cleaned]);
    setPhoneInput('');
    setPhoneError('');
    return true;
  };

  const removePhone = (phone) => {
    setPhones(phones.filter((p) => p !== phone));
  };

  const formatPhone = (p) =>
    p.length === 10 ? p.match(/.{1,2}/g).join(' ') : p;

  const nextStep = () => {
    if (step === 1 && !needsDetailsStep) {
      setStep(3);
    } else {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step === 3 && !needsDetailsStep) {
      setStep(1);
    } else {
      setStep(step - 1);
    }
  };

  const submit = async () => {
    if (phones.length === 0) return;
    setLoading(true);
    const message = getNotifMessage();
    const titleOverride = productType === 'custom' ? customTitle.trim() : null;
    let sentCount = 0;
    let failedCount = 0;
    const results = await Promise.allSettled(
      phones.map((phone) =>
        api.post(
          '/order-status/notify',
          {
            client_phone: phone,
            client_name: clientName,
            product_type: productType,
            mutuelle: mutuelle === 'yes',
            message,
            title: titleOverride || undefined,
          },
          { headers: tenantHeaders() }
        )
      )
    );
    for (const r of results) {
      if (r.status === 'fulfilled') sentCount++;
      else failedCount++;
    }
    setSendResult({ sent: sentCount, failed: failedCount, total: phones.length });
    setSent(true);
    setLoading(false);
  };

  const reset = () => {
    setStep(1);
    setProductType(null);
    setMutuelle(null);
    setCustomTitle('');
    setCustomMessage('');
    setPhones([]);
    setPhoneInput('');
    setPhoneError('');
    setSent(false);
    setSendResult(null);
  };

  // ─── SUCCESS ───
  if (sent) {
    const r = sendResult || { sent: 0, failed: 0, total: 0 };
    const allOk = r.failed === 0;
    return (
      <ProLayout title="Commande prête">
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
            <div
              className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: allOk ? '#10B98115' : '#F59E0B15' }}
            >
              <FaCheck className="w-7 h-7" style={{ color: allOk ? '#10B981' : '#F59E0B' }} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {allOk
                ? r.total > 1
                  ? `${r.sent} notifications envoyées !`
                  : 'Notification envoyée !'
                : `${r.sent}/${r.total} notification${r.total > 1 ? 's' : ''} envoyée${r.sent > 1 ? 's' : ''}`}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {allOk
                ? r.total > 1
                  ? `${r.total} clients ont été prévenus que leur commande est prête.`
                  : 'Votre client a bien été prévenu que sa commande est prête.'
                : `${r.failed} envoi${r.failed > 1 ? 's ont' : ' a'} échoué. Vérifiez les numéros et réessayez si besoin.`}
            </p>
            <button
              onClick={reset}
              className="px-5 py-2.5 text-white text-sm font-semibold rounded-xl hover:opacity-90"
              style={{ backgroundColor: brand }}
            >
              Notifier d'autres clients
            </button>
          </div>
        </div>
      </ProLayout>
    );
  }

  return (
    <ProLayout title="Commande prête">
      <div className="p-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* ═══ LEFT: Form (3 cols) ═══ */}
          <div className="lg:col-span-3 space-y-4">

            {/* Info banner */}
            <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
              <FaInfoCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[0.8125rem] font-semibold text-blue-900">Comment ça marche ?</p>
                <p className="text-[0.6875rem] text-blue-700 mt-0.5 leading-relaxed">
                  Choisissez le type de commande, renseignez les infos du client, et nous envoyons automatiquement une notification personnalisée sur son téléphone (SMS ou push si l'app est installée).
                </p>
              </div>
            </div>

            {/* Stepper */}
            <div className="flex items-center gap-2">
              {[1, 2, 3].filter(s => !(s === 2 && !needsDetailsStep)).map((s, i, arr) => {
                const step2Label = productType === 'custom' ? 'Message' : 'Mutuelle';
                return (
                  <React.Fragment key={s}>
                    <div className={`flex items-center gap-2 ${step >= s ? '' : 'opacity-40'}`}>
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[0.6875rem] font-bold transition-all ${step > s ? 'text-white' : step === s ? 'text-white' : 'bg-gray-200 text-gray-400'}`}
                        style={step >= s ? { backgroundColor: brand } : {}}
                      >
                        {step > s ? <FaCheck className="w-2.5 h-2.5" /> : i + 1}
                      </div>
                      <span className={`text-[0.6875rem] font-semibold ${step >= s ? 'text-gray-900' : 'text-gray-400'}`}>
                        {s === 1 ? 'Type' : s === 2 ? step2Label : 'Client'}
                      </span>
                    </div>
                    {i < arr.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
                  </React.Fragment>
                );
              })}
            </div>

            {/* ─── STEP 1: Product type ─── */}
            {step === 1 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Quel type de commande ?</h3>
                  <p className="text-[0.6875rem] text-gray-400 mt-0.5">Le message sera adapté au produit</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PRODUCT_TYPES.map(p => {
                    const selected = productType === p.key;
                    return (
                      <button
                        key={p.key}
                        onClick={() => setProductType(p.key)}
                        className="flex flex-col items-center gap-2 py-5 px-3 rounded-xl border-2 transition-all text-center"
                        style={selected
                          ? { backgroundColor: brand + '08', borderColor: brand }
                          : { backgroundColor: '#fff', borderColor: '#e5e7eb' }
                        }
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: selected ? brand + '18' : '#f3f4f6' }}
                        >
                          <p.icon className="w-4 h-4" style={{ color: selected ? brand : '#9ca3af' }} />
                        </div>
                        <div>
                          <p className={`text-[0.75rem] font-semibold ${selected ? 'text-gray-900' : 'text-gray-700'}`}>{p.label}</p>
                          <p className="text-[0.625rem] text-gray-400 mt-0.5">{p.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ─── STEP 2: Mutuelle (only for glasses) ─── */}
            {step === 2 && productType === 'glasses' && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Monture + verres remboursés par la mutuelle ?</h3>
                  <p className="text-[0.6875rem] text-gray-400 mt-0.5 leading-relaxed">
                    Cette information reste entre vous et nous. Elle nous permet de relancer automatiquement le client dans un an, au moment où il peut bénéficier d'un nouveau remboursement mutuelle.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'yes', label: 'Oui, avec mutuelle', desc: 'Relance dans 1 an' },
                    { key: 'no', label: 'Non, sans mutuelle', desc: 'Pas de relance' },
                  ].map(o => {
                    const selected = mutuelle === o.key;
                    return (
                      <button
                        key={o.key}
                        onClick={() => setMutuelle(o.key)}
                        className="flex flex-col items-start gap-1 p-4 rounded-xl border-2 text-left transition-all"
                        style={selected
                          ? { backgroundColor: brand + '08', borderColor: brand }
                          : { backgroundColor: '#fff', borderColor: '#e5e7eb' }
                        }
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                            style={{ borderColor: selected ? brand : '#d1d5db' }}
                          >
                            {selected && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: brand }} />}
                          </div>
                          <p className={`text-[0.8125rem] font-semibold ${selected ? 'text-gray-900' : 'text-gray-700'}`}>{o.label}</p>
                        </div>
                        <p className="text-[0.6875rem] text-gray-400 ml-6">{o.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ─── STEP 2 bis : message personnalisé (Autre) ─── */}
            {step === 2 && productType === 'custom' && (() => {
              const titleLen = customTitle.length;
              const msgLen = customMessage.length;
              const titleRemaining = TITLE_MAX_CHARS - titleLen;
              const msgRemaining = SMS_MAX_CHARS - msgLen;
              return (
                <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-5">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Votre message</h3>
                    <p className="text-[0.6875rem] text-gray-400 mt-0.5 leading-relaxed">
                      Le <strong>titre</strong> apparaît en haut de la notification push (et est ignoré pour le SMS). Le <strong>message</strong> est limité à 160 caractères pour tenir sur 1 SMS et est envoyé tel quel.
                    </p>
                  </div>

                  {/* Titre */}
                  <div>
                    <label className="block text-[0.6875rem] font-semibold text-gray-500 mb-1.5">
                      Titre de la notification
                    </label>
                    <input
                      type="text"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value.slice(0, TITLE_MAX_CHARS))}
                      placeholder="Ex : Promotion exceptionnelle"
                      maxLength={TITLE_MAX_CHARS}
                      className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                    <div className="flex items-center justify-between mt-1.5">
                      <p className="text-[0.625rem] text-gray-400">Visible uniquement sur la notification push</p>
                      <p className={`text-[0.625rem] font-semibold ${titleRemaining < 5 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {titleLen}/{TITLE_MAX_CHARS}
                      </p>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-[0.6875rem] font-semibold text-gray-500 mb-1.5">
                      Message
                    </label>
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value.slice(0, SMS_MAX_CHARS))}
                      placeholder="Bonjour, votre commande personnalisée est prête chez nous. À très vite !"
                      rows={5}
                      maxLength={SMS_MAX_CHARS}
                      className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
                    />
                    <div className="flex items-center justify-between mt-1.5">
                      <p className="text-[0.625rem] text-gray-400">
                        {msgLen === 0 ? 'Tapez votre message…' : '1 SMS si moins de 160 caractères.'}
                      </p>
                      <p className={`text-[0.625rem] font-semibold ${msgRemaining < 20 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {msgLen}/{SMS_MAX_CHARS}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ─── STEP 3: Client phones (bulk) ─── */}
            {step === 3 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Téléphones des clients</h3>
                  <p className="text-[0.6875rem] text-gray-400 mt-0.5 leading-relaxed">
                    Ajoutez autant de numéros que nécessaire — la même notification sera envoyée à chacun.
                  </p>
                </div>

                {/* Input + bouton ajouter */}
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      value={phoneInput}
                      onChange={(e) => {
                        setPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 10));
                        if (phoneError) setPhoneError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addPhone(phoneInput);
                        }
                      }}
                      placeholder="06 12 34 56 78"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      className="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 font-mono tracking-wider"
                    />
                    <button
                      onClick={() => addPhone(phoneInput)}
                      disabled={phoneInput.length !== 10}
                      className="flex items-center gap-1.5 px-4 py-2.5 text-white text-sm font-semibold rounded-xl disabled:opacity-40 hover:opacity-90"
                      style={{ backgroundColor: brand }}
                    >
                      <FaPlus className="w-3 h-3" />
                      Ajouter
                    </button>
                  </div>
                  {phoneError ? (
                    <p className="text-[0.625rem] text-red-500 mt-1.5">{phoneError}</p>
                  ) : (
                    <p className="text-[0.625rem] text-gray-400 mt-1.5">
                      Tapez 10 chiffres puis Entrée ou « Ajouter »
                    </p>
                  )}
                </div>

                {/* Liste des numéros ajoutés */}
                {phones.length > 0 && (
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between mb-2.5">
                      <p className="text-[0.6875rem] font-semibold text-gray-500 uppercase tracking-wider">
                        {phones.length} destinataire{phones.length > 1 ? 's' : ''}
                      </p>
                      {phones.length > 1 && (
                        <button
                          onClick={() => setPhones([])}
                          className="text-[0.625rem] text-gray-400 hover:text-red-500 underline"
                        >
                          Tout retirer
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {phones.map((p) => (
                        <span
                          key={p}
                          className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg text-[0.75rem] font-mono tracking-wider bg-gray-100 text-gray-700"
                        >
                          {formatPhone(p)}
                          <button
                            onClick={() => removePhone(p)}
                            className="w-4 h-4 flex items-center justify-center rounded hover:bg-gray-300 text-gray-500 hover:text-gray-700"
                            title="Retirer"
                          >
                            <FaTimes className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button
                  onClick={prevStep}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Retour
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={nextStep}
                  disabled={!canGoNext()}
                  className="flex items-center gap-2 ml-auto px-5 py-2.5 text-white text-sm font-semibold rounded-xl disabled:opacity-40 hover:opacity-90"
                  style={{ backgroundColor: brand }}
                >
                  Continuer <FaArrowRight className="w-3 h-3" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!canGoNext() || loading}
                  className="flex items-center gap-2 ml-auto px-5 py-2.5 text-white text-sm font-semibold rounded-xl disabled:opacity-40 hover:opacity-90"
                  style={{ backgroundColor: brand }}
                >
                  <FaPaperPlane className="w-3 h-3" />
                  {loading
                    ? `Envoi… (${phones.length})`
                    : phones.length > 1
                      ? `Envoyer à ${phones.length} clients`
                      : 'Envoyer la notification'}
                </button>
              )}
            </div>
          </div>

          {/* ═══ RIGHT: iPhone mockup (2 cols) ═══ */}
          <div className="lg:col-span-2">
            <p className="text-[0.6875rem] font-semibold text-gray-400 uppercase tracking-wider mb-3">Aperçu sur mobile</p>

            {/* iPhone 15 Pro mockup */}
            <div className="mx-auto" style={{ width: 280 }}>
              {/* Outer titanium frame */}
              <div
                className="relative mx-auto"
                style={{
                  width: 280,
                  height: 570,
                  borderRadius: 48,
                  background: 'linear-gradient(145deg, #2a2a2c 0%, #1c1c1e 50%, #0a0a0a 100%)',
                  padding: 4,
                  boxShadow: '0 30px 60px -15px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05), inset 0 0 0 1px rgba(255,255,255,0.08)',
                }}
              >
                {/* Inner bezel */}
                <div
                  className="relative w-full h-full overflow-hidden"
                  style={{
                    borderRadius: 44,
                    background: '#000',
                    padding: 2,
                  }}
                >
                  {/* Screen */}
                  <div
                    className="relative w-full h-full overflow-hidden"
                    style={{
                      borderRadius: 42,
                      backgroundImage: `linear-gradient(180deg, #1a1a2e 0%, #0f0f1e 50%, #000000 100%)`,
                    }}
                  >
                    {/* Wallpaper subtle pattern */}
                    <div
                      className="absolute inset-0 opacity-30"
                      style={{
                        background: `radial-gradient(ellipse at top, ${brand}22 0%, transparent 50%), radial-gradient(ellipse at bottom, ${brand}15 0%, transparent 60%)`,
                      }}
                    />

                    {/* Dynamic Island */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 z-20"
                      style={{
                        top: 12,
                        width: 110,
                        height: 32,
                        background: '#000',
                        borderRadius: 20,
                      }}
                    />

                    {/* Status bar */}
                    <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-7 pt-4">
                      <span className="text-[0.8125rem] text-white font-semibold" style={{ letterSpacing: -0.3 }}>9:41</span>
                      <div className="flex items-center gap-1">
                        {/* Signal bars */}
                        <svg width="16" height="11" viewBox="0 0 16 11" fill="white">
                          <rect x="0" y="7" width="3" height="4" rx="0.5" />
                          <rect x="4" y="5" width="3" height="6" rx="0.5" />
                          <rect x="8" y="2" width="3" height="9" rx="0.5" />
                          <rect x="12" y="0" width="3" height="11" rx="0.5" />
                        </svg>
                        {/* WiFi */}
                        <svg width="14" height="11" viewBox="0 0 14 11" fill="white">
                          <path d="M7 11L9.1 8.7C8.5 8.2 7.8 8 7 8C6.2 8 5.5 8.2 4.9 8.7L7 11ZM11.5 5.9L13 4.3C11.4 2.7 9.3 1.8 7 1.8C4.7 1.8 2.6 2.7 1 4.3L2.5 5.9C3.7 4.7 5.3 4 7 4C8.7 4 10.3 4.7 11.5 5.9ZM2.5 5.9" />
                        </svg>
                        {/* Battery */}
                        <div className="flex items-center">
                          <div className="relative border border-white/80 rounded" style={{ width: 22, height: 10, padding: 1 }}>
                            <div className="h-full bg-white rounded-sm" style={{ width: '80%' }} />
                          </div>
                          <div className="bg-white/80" style={{ width: 1, height: 4, borderRadius: '0 1px 1px 0' }} />
                        </div>
                      </div>
                    </div>

                    {/* Lock screen content */}
                    <div className="absolute inset-0 flex flex-col items-center pt-20 px-5">
                      {/* Date */}
                      <p className="text-[0.8125rem] text-white/80 font-medium">mercredi 9 avril</p>
                      {/* Time */}
                      <p className="text-white font-thin mt-0.5" style={{ fontSize: 84, lineHeight: 0.95, letterSpacing: -3 }}>
                        9:41
                      </p>

                      {/* Notification area */}
                      <div className="w-full mt-8">
                        {productType ? (
                          <div
                            className="w-full backdrop-blur-xl rounded-2xl p-3 transform transition-all duration-300"
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.85)',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                              WebkitBackdropFilter: 'blur(20px)',
                            }}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {brandLogo ? (
                                <img src={brandLogo} alt="" className="w-6 h-6 rounded-md object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: brand }}>
                                  <FaBell className="w-3 h-3 text-white" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-[0.625rem] font-semibold text-gray-600 uppercase tracking-wide truncate">
                                  {brandName || 'Votre boutique'}
                                </p>
                              </div>
                              <p className="text-[0.625rem] text-gray-500">maintenant</p>
                            </div>
                            <p className="text-[0.75rem] font-semibold text-gray-900 leading-tight mb-0.5">
                              {productType === 'glasses' && 'Vos lunettes sont prêtes'}
                              {productType === 'lenses' && 'Vos lentilles sont disponibles'}
                              {productType === 'sav' && 'Votre SAV est terminé'}
                              {productType === 'custom' && (customTitle.trim() || 'Titre de votre notification')}
                            </p>
                            <p className="text-[0.6875rem] text-gray-700 leading-snug">
                              {productType === 'custom' && !customMessage.trim()
                                ? '(Tapez votre message…)'
                                : getNotifMessage()}
                            </p>
                          </div>
                        ) : (
                          <div className="w-full rounded-2xl p-6 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
                            <FaMobileAlt className="w-5 h-5 text-white/40 mx-auto mb-2" />
                            <p className="text-[0.6875rem] text-white/50">Sélectionnez un type de commande</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bottom home indicator */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 bg-white rounded-full"
                      style={{ bottom: 8, width: 120, height: 4 }}
                    />
                  </div>
                </div>

                {/* Side buttons decoration */}
                {/* Left: Volume + silent */}
                <div
                  className="absolute"
                  style={{ left: -2, top: 100, width: 3, height: 30, background: '#1a1a1a', borderRadius: '2px 0 0 2px' }}
                />
                <div
                  className="absolute"
                  style={{ left: -2, top: 150, width: 3, height: 50, background: '#1a1a1a', borderRadius: '2px 0 0 2px' }}
                />
                <div
                  className="absolute"
                  style={{ left: -2, top: 210, width: 3, height: 50, background: '#1a1a1a', borderRadius: '2px 0 0 2px' }}
                />
                {/* Right: Power */}
                <div
                  className="absolute"
                  style={{ right: -2, top: 160, width: 3, height: 70, background: '#1a1a1a', borderRadius: '0 2px 2px 0' }}
                />
              </div>
            </div>

            <p className="text-[0.6875rem] text-gray-400 text-center mt-5 leading-relaxed">
              Le client recevra cette notification sur son téléphone<br />(ou un SMS si l'app n'est pas installée)
            </p>
          </div>
        </div>
      </div>
    </ProLayout>
  );
};

export default OrderNotifyPage;
